/**
 * Google Reviews Sync - Hosted on Shopify
 * 
 * This script fetches Google Reviews using the Google Maps JavaScript API
 * and displays them directly on your site. No backend required!
 * 
 * Setup:
 * 1. Get Google Maps API key (with Places API enabled)
 * 2. Get your Google Place ID
 * 3. Update CONFIG below
 * 4. Include this script in your section
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION - Can be set via window.GOOGLE_REVIEWS_CONFIG or defaults below
  // ============================================
  const CONFIG = window.GOOGLE_REVIEWS_CONFIG || {
    GOOGLE_API_KEY: 'AIzaSyB7YeEj8aT2wAtZhWNWm0AHQ64jEXThxKw',  // Your Google Maps API key
    PLACE_ID: 'ChIJG8hPzIBz4YkRgf1letgZljU',                   // Ted's Shoe & Sport Place ID
    MIN_RATING: 4,                                              // Only show 4-5 star reviews
    AUTO_SYNC: true,                                             // Auto-fetch on page load
    UPDATE_METAFIELD: false,                                     // Set to true if you have a proxy endpoint
    METAFIELD_PROXY_URL: ''                                      // Your proxy URL (optional)
  };

  // Retry tracking
  let retryCount = 0;
  const MAX_RETRIES = 10; // Maximum number of retries before giving up

  // ============================================
  // MAIN FUNCTIONS
  // ============================================

  /**
   * Initialize Google Reviews using the new Places API
   */
  async function initGoogleReviews() {
    // Check if we're in Shopify Theme Editor (skip API loading in editor)
    if (window.Shopify && window.Shopify.designMode) {
      console.log('Shopify Theme Editor detected - skipping Google Maps API load');
      return;
    }

    // Check if Google Maps API is loaded
    if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
      retryCount++;
      if (retryCount >= MAX_RETRIES) {
        console.error('Google Maps API failed to load after ' + MAX_RETRIES + ' attempts. Using cached data if available.');
        // Try to use cached data
        const cached = localStorage.getItem('google_reviews_cache');
        if (cached) {
          try {
            const cache = JSON.parse(cached);
            updatePageDisplay(cache.data);
          } catch (e) {
            handleError('API_LOAD_FAILED');
          }
        } else {
          handleError('API_LOAD_FAILED');
        }
        return;
      }
      setTimeout(initGoogleReviews, 500);
      return;
    }

    // Reset retry count on success
    retryCount = 0;

    // Check if new Places API is available
    if (!google.maps.places.Place) {
      console.error('New Places API (google.maps.places.Place) is not available. Please enable "Places API (New)" in Google Cloud Console.');
      handleError('NEW_API_NOT_AVAILABLE');
      return;
    }

    console.log('Fetching Google Reviews...');

    try {
      // Use the new Places API
      const { Place } = google.maps.places;
      
      // Create a Place instance
      const place = new Place({
        id: CONFIG.PLACE_ID
      });

      // Fetch place details including reviews
      await place.fetchFields({
        fields: ['id', 'displayName', 'rating', 'userRatingCount', 'reviews', 'formattedAddress']
      });

      // Process the place data
      const placeData = {
        rating: place.rating || 0,
        user_ratings_total: place.userRatingCount || 0,
        reviews: place.reviews || []
      };

      handleReviewsData(placeData);

    } catch (error) {
      console.error('Error fetching reviews:', error);
      handleError(error.message || 'FETCH_ERROR');
    }
  }

  /**
   * Process reviews data
   */
  function handleReviewsData(place) {
    const reviews = place.reviews || [];
    const overallRating = place.rating || 0;
    const totalRatings = place.user_ratings_total || 0;

    // Filter for 4-5 star reviews only
    const filteredReviews = reviews.filter(review => {
      const rating = review.rating || review.starRating || 0;
      return rating >= CONFIG.MIN_RATING;
    });

    // Sort by newest first (most recent publishTime)
    filteredReviews.sort((a, b) => {
      const timeA = a.publishTime ? new Date(a.publishTime) : new Date(0);
      const timeB = b.publishTime ? new Date(b.publishTime) : new Date(0);
      return timeB - timeA; // Descending order (newest first)
    });

    // Prepare data structure
    const reviewsData = {
      rating: overallRating,
      total_ratings: totalRatings,
      reviews: filteredReviews.map(review => {
        // Handle both old and new API formats
        const authorName = review.authorAttribution?.displayName || review.author_name || 'Anonymous';
        const rating = review.rating || review.starRating || 5;
        const text = review.text || review.text?.text || '';
        const time = review.publishTime ? formatTimeAgo(review.publishTime) : review.relative_time_description || '';
        const profilePhoto = review.authorAttribution?.photoUri || review.profile_photo_url || '';

        return {
          author_name: authorName,
          rating: rating,
          text: text,
          time: time,
          profile_photo_url: profilePhoto
        };
      }),
      last_updated: new Date().toISOString()
    };

    console.log(`✅ Fetched ${filteredReviews.length} reviews (${totalRatings} total)`);

    // Update page display
    updatePageDisplay(reviewsData);

    // Optionally update Shopify metafield via proxy
    if (CONFIG.UPDATE_METAFIELD && CONFIG.METAFIELD_PROXY_URL) {
      updateShopifyMetafield(reviewsData);
    }

    // Store in localStorage for caching
    localStorage.setItem('google_reviews_cache', JSON.stringify({
      data: reviewsData,
      timestamp: Date.now()
    }));
  }

  /**
   * Update the page display with reviews
   */
  function updatePageDisplay(reviewsData) {
    const section = document.querySelector('.google-reviews-section');
    if (!section) {
      console.warn('Google Reviews section not found on page');
      return;
    }

    // Update overall rating
    const ratingElement = section.querySelector('.rating-number');
    if (ratingElement) {
      ratingElement.textContent = reviewsData.rating.toFixed(1);
    }

    // Update reviews count
    const countTextElement = section.querySelector('.reviews-count-text');
    if (countTextElement) {
      countTextElement.textContent = `${reviewsData.total_ratings} reviews on Google`;
    }

    // Update AI summary count
    const aiSummaryCount = section.querySelector('.ai-summary-count');
    if (aiSummaryCount) {
      aiSummaryCount.textContent = reviewsData.total_ratings;
    }

    // Update stars
    updateStars(section, reviewsData.rating);

    // Update reviews carousel
    const carousel = section.querySelector('.google-reviews-carousel');
    if (carousel) {
      // Clear existing placeholder or error messages
      const placeholder = carousel.querySelector('.reviews-loading-placeholder');
      if (placeholder) placeholder.remove();
      const error = carousel.querySelector('.reviews-error');
      if (error) error.remove();

      // Clear existing reviews (if any)
      const existingCards = carousel.querySelectorAll('.google-review-card');
      existingCards.forEach(card => card.remove());

      // Add reviews if available
      if (reviewsData.reviews.length > 0) {
        // Add new review cards from API
        reviewsData.reviews.forEach(review => {
          const card = createReviewCard(review);
          carousel.appendChild(card);
        });
      }

      // Add manual reviews after API reviews
      const sectionId = section.id.replace('google-reviews-', '');
      const manualReviews = window.MANUAL_REVIEWS_DATA && window.MANUAL_REVIEWS_DATA[sectionId];
      if (manualReviews && manualReviews.length > 0) {
        manualReviews.forEach(review => {
          const card = createReviewCard(review, true); // Pass true to indicate manual review
          carousel.appendChild(card);
        });
      }

      // Check if we have any reviews at all
      const totalReviews = (reviewsData.reviews.length || 0) + (manualReviews ? manualReviews.length : 0);
      if (totalReviews === 0) {
        // Show message if no reviews
        const noReviews = document.createElement('div');
        noReviews.className = 'reviews-error';
        noReviews.innerHTML = '<p>No 4-5 star reviews available at this time.</p>';
        carousel.appendChild(noReviews);
      } else {
        // Initialize truncation and modal for dynamically added reviews
        if (typeof initReviewTruncation === 'function') {
          initReviewTruncation(sectionId);
        }
        if (typeof initReviewModal === 'function') {
          initReviewModal(sectionId);
        }
      }
    }
  }

  /**
   * Update star rating display
   */
  function updateStars(section, rating) {
    const starsContainer = section.querySelector('.rating-stars .stars-display');
    if (!starsContainer) return;

    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;
    const displayStars = hasHalf ? fullStars + 1 : fullStars;

    let starsHTML = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= displayStars) {
        starsHTML += '<span style="color: var(--google-reviews-accent) !important;">★</span>';
      } else {
        starsHTML += '<span style="display: none;">☆</span>';
      }
    }

    starsContainer.innerHTML = starsHTML;
  }

  /**
   * Create a review card element
   * @param {Object} review - The review data
   * @param {boolean} isManual - Whether this is a manual review (no Google badge)
   */
  function createReviewCard(review, isManual = false) {
    const card = document.createElement('div');
    card.className = 'google-review-card';
    if (isManual) card.classList.add('manual-review');
    card.dataset.reviewId = Math.random().toString(36);

    const authorInitial = (review.author_name || 'A').charAt(0).toUpperCase();
    const stars = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
    const fullText = review.text || '';
    const maxLength = 200;
    const shouldTruncate = fullText.length > maxLength;
    const displayText = shouldTruncate ? fullText.substring(0, maxLength) + '...' : fullText;

    // Check if this manual review should show Google badge
    const showGoogleBadge = !isManual || (isManual && review.show_google_badge);

    // Store review data for modal
    const reviewData = {
      author: review.author_name || 'Anonymous',
      rating: stars,
      time: review.time || '',
      avatar: review.profile_photo_url || '',
      text: fullText,
      isManual: isManual,
      showGoogleBadge: showGoogleBadge
    };

    // Google badge HTML (for API reviews or manual reviews with badge enabled)
    const googleBadgeHTML = showGoogleBadge ? `
          <div class="google-badge">
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
          </div>` : '';

    // Verified badge - show checkmark for Google reviews or manual reviews with badge enabled
    const verifiedBadgeHTML = showGoogleBadge ? `
            <svg class="review-verified" viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>` : '';

    card.innerHTML = `
      <div class="review-card-header">
        <div class="review-avatar">
          ${review.profile_photo_url
            ? `<img src="${review.profile_photo_url}" alt="${review.author_name}" loading="lazy">`
            : `<span>${authorInitial}</span>`
          }
          ${googleBadgeHTML}
        </div>
        <div class="review-author-info">
          <div class="review-author-name">
            ${review.author_name}
            ${verifiedBadgeHTML}
          </div>
          ${review.time ? `<div class="review-time">${review.time}</div>` : ''}
        </div>
      </div>
      <div class="review-rating" style="color: var(--google-reviews-accent);">
        ${stars}
      </div>
      <div class="review-text ${shouldTruncate ? 'truncated' : ''}">${displayText}</div>
    `;

    // Add Read More button if truncated
    if (shouldTruncate) {
      const reviewTextDiv = card.querySelector('.review-text');
      const readMoreBtn = document.createElement('button');
      readMoreBtn.className = 'read-more-btn';
      readMoreBtn.textContent = 'Read More';
      readMoreBtn.dataset.reviewData = JSON.stringify(reviewData);
      reviewTextDiv.parentElement.appendChild(readMoreBtn);
    }

    // Store full review data on card for modal access
    card.dataset.reviewData = JSON.stringify(reviewData);

    return card;
  }

  /**
   * Update Shopify metafield via proxy (optional)
   */
  async function updateShopifyMetafield(reviewsData) {
    if (!CONFIG.METAFIELD_PROXY_URL) return;

    try {
      const response = await fetch(CONFIG.METAFIELD_PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reviews: reviewsData,
          storeDomain: window.Shopify?.shop || 'tedsports.myshopify.com'
        })
      });

      if (response.ok) {
        console.log('✅ Reviews synced to Shopify metafields');
      } else {
        console.warn('⚠️ Failed to sync to metafields (this is optional)');
      }
    } catch (error) {
      console.warn('⚠️ Metafield sync failed (this is optional):', error);
    }
  }

  /**
   * Format time ago from Date or timestamp
   */
  function formatTimeAgo(dateOrTimestamp) {
    if (!dateOrTimestamp) return '';
    
    const date = dateOrTimestamp instanceof Date ? dateOrTimestamp : new Date(dateOrTimestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }

  /**
   * Handle errors
   */
  function handleError(status) {
    const section = document.querySelector('.google-reviews-section');
    if (section) {
      const errorDiv = document.createElement('div');
      errorDiv.className = 'google-reviews-error';
      errorDiv.style.cssText = 'padding: 2rem; text-align: center; color: #666;';
      errorDiv.innerHTML = `
        <p>Unable to load reviews at this time.</p>
        <p><small>Error: ${status}</small></p>
        <button onclick="location.reload()" style="margin-top: 1rem; padding: 0.5rem 1rem; cursor: pointer;">Retry</button>
      `;
      
      const container = section.querySelector('.google-reviews-container') || section;
      container.appendChild(errorDiv);
    }
  }

  /**
   * Load Google Maps JavaScript API
   */
  function loadGoogleMapsAPI() {
    // Check if we're in Shopify Theme Editor (skip API loading in editor)
    if (window.Shopify && window.Shopify.designMode) {
      console.log('Shopify Theme Editor detected - skipping Google Maps API load');
      return;
    }

    // Check if already loaded
    if (typeof google !== 'undefined' && google.maps && google.maps.places) {
      retryCount = 0; // Reset retry count
      initGoogleReviews();
      return;
    }

    // Check for cached data first
    const cached = localStorage.getItem('google_reviews_cache');
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        const cacheAge = Date.now() - cache.timestamp;
        const maxAge = 60 * 60 * 1000; // 1 hour

        if (cacheAge < maxAge) {
          console.log('Using cached reviews');
          updatePageDisplay(cache.data);
          
          // Still fetch fresh data in background
          if (CONFIG.AUTO_SYNC) {
            setTimeout(initGoogleReviews, 1000);
          }
          return;
        }
      } catch (e) {
        console.warn('Cache invalid, fetching fresh data');
      }
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
      // Script already exists, just wait for it
      return;
    }

    // Create script tag - using new Places API
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${CONFIG.GOOGLE_API_KEY}&libraries=places&loading=async&callback=initGoogleReviewsCallback`;
    script.async = true;
    script.defer = true;

    // Handle script load errors
    script.onerror = function() {
      console.error('Failed to load Google Maps API script');
      retryCount = MAX_RETRIES; // Prevent further retries
      handleError('SCRIPT_LOAD_FAILED');
    };

    // Set global callback
    window.initGoogleReviewsCallback = function() {
      retryCount = 0; // Reset retry count on success
      initGoogleReviews();
    };

    // Add to page
    document.head.appendChild(script);
  }

  /**
   * Manual sync function (can be called from button)
   */
  window.syncGoogleReviews = function() {
    localStorage.removeItem('google_reviews_cache');
    loadGoogleMapsAPI();
  };

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      if (CONFIG.AUTO_SYNC) {
        loadGoogleMapsAPI();
      }
    });
  } else {
    if (CONFIG.AUTO_SYNC) {
      loadGoogleMapsAPI();
    }
  }

})();



